
import { cn } from '@/lib/utils';

export interface ErrorStateProps {
  /** Error message to display */
  message?: string;
  /** Callback to retry the failed operation */
  onRetry?: () => void;
  /** Callback to navigate back home */
  onGoHome?: () => void;
}

/**
 * ErrorState Component
 * Displayed when an error occurs loading prayers
 * Provides retry and navigation options
 */
export function ErrorState({ 
  message = 'Unable to load prayers. Please try again.',
  onRetry,
  onGoHome
}: ErrorStateProps) {
  return (
    <div 
      className={cn(
        "bg-white dark:bg-slate-800 rounded-2xl shadow-xl",
        "p-8 sm:p-12 max-w-md w-full mx-auto text-center",
        "animate-fade-in"
      )}
      role="alert"
      aria-live="assertive"
    >
      {/* Error icon */}
      <div className="mb-6">
        <div 
          className={cn(
            "w-20 h-20 mx-auto rounded-full",
            "bg-red-100 dark:bg-red-900/30",
            "flex items-center justify-center"
          )}
        >
          <svg 
            className="w-10 h-10 text-red-600 dark:text-red-400"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      </div>

      {/* Error message */}
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
        Something Went Wrong
      </h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8">
        {message}
      </p>

      {/* Action buttons */}
      <div className="space-y-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className={cn(
              "w-full py-3 px-6",
              "bg-primary-600 hover:bg-primary-700 active:bg-primary-800",
              "text-white font-semibold rounded-xl",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
              "dark:focus:ring-offset-slate-800",
              "flex items-center justify-center gap-2"
            )}
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Try Again
          </button>
        )}
        {onGoHome && (
          <button
            onClick={onGoHome}
            className={cn(
              "w-full py-3 px-6",
              "bg-slate-100 hover:bg-slate-200 active:bg-slate-300",
              "dark:bg-slate-700 dark:hover:bg-slate-600 dark:active:bg-slate-500",
              "text-slate-700 dark:text-slate-200 font-semibold rounded-xl",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2",
              "dark:focus:ring-offset-slate-800",
              "flex items-center justify-center gap-2"
            )}
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Go Home
          </button>
        )}
      </div>

      {/* Help text */}
      <p className="mt-8 text-sm text-slate-500 dark:text-slate-500">
        If this problem persists, please contact support.
      </p>
    </div>
  );
}

export default ErrorState;
